import * as tslib_1 from "tslib";
import { AfterViewInit, ChangeDetectorRef, ElementRef, EventEmitter, NgZone, OnDestroy, OnInit, Renderer2, Type } from '@angular/core';
import { Subject } from 'rxjs';
import { CsAgGridJsAngularComponentResolverService } from '@app/shared/cs-ag-grid-js/cs-ag-grid-js-angular-component-resolver.service';
import { environment } from '../../../environments/environment';
import cloneDeep from 'lodash.clonedeep';
var CsAgGridJsComponent = /** @class */ (function () {
    function CsAgGridJsComponent(_componentResolver, renderer, cdr, ngZone) {
        var _this = this;
        this._componentResolver = _componentResolver;
        this.renderer = renderer;
        this.cdr = cdr;
        this.ngZone = ngZone;
        this.instanceId = ++CsAgGridJsComponent.instanceSeq;
        this.onGridInit = new EventEmitter();
        this.onGridDataLoad = new EventEmitter();
        this.onAllRowSelection = new EventEmitter();
        this.onRowSelection = new EventEmitter();
        // @Output() sendParentNode = new EventEmitter()
        this.filterWidthChangeEvent = new Subject();
        this.appliedFilter = [];
        this.searchText = '';
        /** Extra keys (e.g. userId, tagIds) when the search bar's "@"/"#" dropdowns are enabled and it emits
         * an object instead of a plain string. */
        this.searchExtraParams = {};
        this.paginationParams = {};
        /** Shown until `createGrid()` succeeds; avoids relying on `gridApi` alone for *ngIf + forces CD after hide. */
        this.showGridSkeleton = true;
        this.gridLayout = 'autoHeight';
        this.isDataLoading = true;
        this.gridApiMap = new Map();
        this.currentlySelectedRows = [];
        this.isSelectionLoading = false;
        this.isDetailGrid = false;
        this.swapRows = function (event) {
            if (!event.node
                || !event.overNode
                || !event.vDirection
                || (event.node.id === event.overNode.id)) {
                return;
            }
            requestAnimationFrame(function () {
                var draggedRowElement = _this.grid.nativeElement.querySelector("[row-id=\"" + event.node.id + "\"]");
                var overRowElement = _this.grid.nativeElement.querySelector("[row-id=\"" + event.overNode.id + "\"]");
                var draggingRowIndex = draggedRowElement.getAttribute('row-index');
                var overRowIndex = overRowElement.getAttribute('row-index');
                if ((event.vDirection === 'down' && (draggingRowIndex < overRowIndex))
                    || (event.vDirection === 'up' && (draggingRowIndex > overRowIndex))) {
                    var rowHeight = event.node.rowHeight;
                    var draggingRowPos = +draggingRowIndex * rowHeight;
                    var overRowPos = +overRowIndex * rowHeight;
                    _this.renderer.setAttribute(draggedRowElement, 'row-index', overRowIndex);
                    _this.renderer.setAttribute(overRowElement, 'row-index', draggingRowIndex);
                    _this.renderer.setStyle(draggedRowElement, 'transform', "translateY(" + overRowPos + "px)");
                    _this.renderer.setStyle(overRowElement, 'transform', "translateY(" + (draggingRowPos) + "px)");
                    _this.updateSwappingRowClasses(event, draggedRowElement, +overRowIndex);
                    _this.updateSwappingRowClasses(event, overRowElement, +draggingRowIndex);
                    event.node.data['swappedOrder'] = +overRowIndex;
                    event.overNode.data['swappedOrder'] = +draggingRowIndex;
                }
            });
        };
        this.getDisplayedRowNodes = function (api) {
            if (!api || typeof api.getRenderedNodes !== 'function') {
                return [];
            }
            try {
                return api.getRenderedNodes() || [];
            }
            catch (error) {
                console.warn('Error getting displayed row nodes:', error);
                return [];
            }
        };
        this.getSelectedRows = function () {
            if (!_this.gridApi) {
                return _this.currentlySelectedRows.length ? _this.currentlySelectedRows.slice() : [];
            }
            // Use getSelectedNodes() so count matches grid selection (getRenderedNodes only returns visible rows)
            var selectedNodes = _this.gridApi.getSelectedNodes ? _this.gridApi.getSelectedNodes() : [];
            var selectedDataFromGrid = (selectedNodes || [])
                .filter(function (node) { return node && node.data; })
                .map(function (node) { return node.data; });
            var keyField = 'uniqueRowKeyField' in _this.config.gridConfig ? _this.config.gridConfig.uniqueRowKeyField : undefined;
            if (keyField) {
                var rowMap_1 = new Map();
                [_this.currentlySelectedRows, selectedDataFromGrid].forEach(function (rows) {
                    (rows || []).forEach(function (row) {
                        var key = row[keyField];
                        if (key !== undefined && key !== null && key !== '') {
                            rowMap_1.set(key, row);
                        }
                    });
                });
                var deduplicated = Array.from(rowMap_1.values());
                _this.currentlySelectedRows = deduplicated;
                return deduplicated;
            }
            return selectedDataFromGrid;
        };
        this.getAddOnData = function () {
            return _this.gridListAddOnData || null;
        };
        this.showOrHideNoRowsOverlay = function (show) { return _this.gridApi[show ? 'showNoRowsOverlay' : 'hideOverlay'](); };
        this.refreshGridData = function () {
            _this.showOrHideNoRowsOverlay(false);
            if (_this.isCSRMEnabled) {
                _this.setCSRMData();
            }
            else {
                _this.gridApi.refreshServerSide({ route: [], purge: true });
                _this.gridApi.setGridOption('serverSideDatasource', _this.dataSource);
            }
            _this.resetSelectedRows();
        };
        this.resetPagination = function () {
            if (_this.isPagination) {
                _this.goToPage(1);
                _this.totalRecords = null;
                _this.isDataLoading = true;
            }
        };
    }
    /** Dev-only lifecycle tracing: filter console by `[CsAgGridJs` */
    CsAgGridJsComponent.prototype.dbg = function (message, detail) {
        if (detail !== undefined) {
            console.log('[CsAgGridJs #' + this.instanceId + '] ' + message, detail);
        }
        else {
            console.log('[CsAgGridJs #' + this.instanceId + '] ' + message);
        }
    };
    CsAgGridJsComponent.prototype.ngOnInit = function () {
        this.dbg('init: component setup (config clone, options, license)');
        this.cloneConfig();
        this.setConfig();
        this.setGridOptions();
        this.setGridLicense();
        this.isDetailGrid = this.params && Array.isArray(this.params.currentParent) && this.params.currentParent.length > 0;
    };
    /**
     * AG Grid is attached to #grid in the template. @ViewChild is only available after the view initializes;
     * creating the grid in ngOnInit silently skipped and never emitted onGridInit, breaking all parents.
     *
     * Defer createGrid/setCSRMData to the next macrotask so the skeleton flag is not cleared in the same CD turn
     * as the first template check. Otherwise dev-mode checkNoChanges throws ExpressionChangedAfterItHasBeenCheckedError.
     */
    CsAgGridJsComponent.prototype.ngAfterViewInit = function () {
        var _this = this;
        this.dbg('afterViewInit: scheduling grid create + initial data (next tick)');
        setTimeout(function () {
            _this.createGrid();
            _this.setCSRMData();
        }, 0);
    };
    CsAgGridJsComponent.prototype.cloneConfig = function () {
        this.config = cloneDeep(this.config);
    };
    CsAgGridJsComponent.prototype.setConfig = function () {
        if (this.config.gridConfig.rowReOrder) {
            this.config.gridConfig.colDefs.unshift({
                field: 'row_drag',
                headerName: '',
                rowDrag: true,
                minWidth: 48,
                maxWidth: 48,
            });
        }
        var dataKeys = this.config.gridConfig.dataResKeys;
        this.config.gridConfig.dataResKeys = {
            countKey: dataKeys ? (dataKeys['countKey'] || 'count') : 'count',
            dataKey: dataKeys ? (dataKeys['dataKey'] || 'data') : 'data'
        };
        this.appliedFilter = this.config.hideFilterPanel ? [] : (this.config.filterPanelConfig['appliedFilter'] || []);
        this.filterWidthChangeEvent.next(this.appliedFilter);
        this.paginationParams['pageSize'] = this.isPagination ? (this.config.gridConfig.rowsPerPage || 5) : null;
    };
    CsAgGridJsComponent.prototype.setGridOptions = function () {
        var _this = this;
        this.gridOptions = tslib_1.__assign({ components: tslib_1.__assign({}, this._componentResolver.csAgCustomComponents(), this.resolvedAngularComponents()), 
            // Note: It will be enabled (or) handled based on future spec, if required.
            // selectionColumnDef: {
            //     pinned: true
            // },
            defaultColDef: {
                flex: 1,
                suppressHeaderMenuButton: true,
                suppressHeaderContextMenu: true,
                sortable: false,
                unSortIcon: true,
                wrapText: this.config.gridConfig.wrapText !== false,
                autoHeight: this.config.gridConfig.rowHeight == null && this.config.gridConfig.wrapText !== false,
                wrapHeaderText: this.config.gridConfig.wrapHeaderText !== false,
                autoHeaderHeight: this.config.gridConfig.wrapHeaderText !== false,
                enableCellChangeFlash: true,
                resizable: this.config.gridConfig.isColumnResizable === true,
                tooltipComponent: this.config.gridConfig.customtooltipComponent,
            }, columnDefs: this.config.gridConfig.colDefs }, (this.config.gridConfig.rowHeight != null && { rowHeight: this.config.gridConfig.rowHeight }), { tooltipShowDelay: this.config.gridConfig.tooltipShowDelay, tooltipShowMode: this.config.gridConfig.tooltipShowMode, autoGroupColumnDef: {
                headerName: 'Group',
                resizable: true,
                minWidth: 268,
                pinned: 'left',
                cellRendererParams: {
                    suppressCount: true,
                    innerRenderer: function (params) {
                        if (params.value) {
                            var count = params.data.groupCount || 0; //data.groupCount
                            var displayValue = params.valueFormatted || params.value;
                            if (!params.valueFormatted && params.node && params.node.rowGroupColumn && params.node.rowGroupColumn.getColDef) {
                                var rowGroupColumn = params.node.rowGroupColumn;
                                var rowGroupColDef = rowGroupColumn.getColDef();
                                if (rowGroupColDef && typeof rowGroupColDef.valueFormatter === 'function') {
                                    try {
                                        displayValue = rowGroupColDef.valueFormatter(tslib_1.__assign({}, params, { value: params.value, data: params.data, node: params.node, colDef: rowGroupColDef, column: rowGroupColumn }));
                                    }
                                    catch (e) {
                                        displayValue = params.value;
                                    }
                                }
                            }
                            return displayValue + " (" + count + ")";
                        }
                    }
                }
            }, domLayout: 'autoHeight', suppressMovableColumns: true, rowClass: this.focusEnablingClass, suppressCellFocus: this.config.gridConfig.rowFocus !== true, suppressServerSideFullWidthLoadingRow: true, masterDetail: !!this.config.gridConfig.enableDetailGrid, suppressContextMenu: true, scrollbarWidth: 4, noRowsOverlayComponent: 'noRowsOverlay', suppressMoveWhenRowDragging: true, suppressPaginationPanel: true, suppressScrollOnNewData: true, blockLoadDebounceMillis: 500, rowGroupPanelShow: this.config.gridConfig.enableRowGroupPanel ? 'always' : 'never', treeData: this.config.gridConfig.treeData, 
            // keepDetailRows: true,
            // keepDetailRowsCount: 1,
            // Note: Below, will be enabled (or) handled based on the future spec, if required.
            // suppressCsvExport: true,
            // suppressExcelExport: true,
            // rowDragMultiRow: true,
            // rowDragEntireRow: true,
            // detailRowAutoHeight: true,
            onGridSizeChanged: function (event) { return _this.updateDomLayout(); }, onCellClicked: function (params) { return _this.onOpenChildGrid(_this.config.gridConfig.isCollapseOtherParents, params); }, onModelUpdated: function (event) { return _this.updateDomLayout(); }, onRowDragMove: function (event) { return _this.swapRows(event); }, onDragStopped: function (event) { return _this.onRowDropped(event); }, onRowSelected: function (event) { return _this.rowSelection(event); }, onSelectionChanged: function (event) { return _this.allRowSelection(event); }, onSortChanged: function (event) { return _this.resetSelectedRows(); }, onCellFocused: this.config.gridConfig.onCellKeyDown, onColumnRowGroupChanged: function () { return _this.resetSelectedRows(); } });
        this.setServerSideProps();
        this.setPaginationProps();
        this.setRowSelectionConfig();
        this.setDetailGridConfig();
    };
    CsAgGridJsComponent.prototype.onOpenChildGrid = function (collapse, params) {
        var _this = this;
        // only act if expand/collapse icon clicked
        if (collapse && params.colDef.cellRenderer === 'detailCellRenderer' && params.node.master) {
            setTimeout(function () {
                if (params.node.expanded) {
                    _this.gridApi.forEachNode(function (node) {
                        if (node !== params.node && node.expanded) {
                            node.setExpanded(false); // collapse others
                            node['expandedColId'] = null;
                        }
                    });
                }
            });
        }
    };
    CsAgGridJsComponent.prototype.setServerSideProps = function () {
        var _this = this;
        if (this.isCSRMEnabled) {
            return;
        }
        this.gridOptions['rowModelType'] = 'serverSide';
        if (this.gridOptions.treeData && this.config.gridConfig.uniqueRowKeyField) {
            this.gridOptions['treeData'] = this.gridOptions.treeData;
            this.gridOptions['isServerSideGroup'] = function (dataItem) {
                return true;
            };
            this.gridOptions['getServerSideGroupKey'] = function (dataItem) {
                return dataItem[_this.config.gridConfig.uniqueRowKeyField];
            };
        }
        this.gridOptions['serverSideStoreType'] = 'partial';
        //this.gridOptions['serverSideStoreType'] = 'full';
        this.gridOptions['serverSideInitialRowCount'] = this.config.gridConfig.loaderRowCount || 5;
        this.gridOptions['serverSideDatasource'] = this.dataSource;
        this.gridOptions['maxConcurrentDatasourceRequests'] = 1;
    };
    CsAgGridJsComponent.prototype.setPaginationProps = function () {
        if (this.isPagination) {
            this.gridOptions['pagination'] = true;
            this.gridOptions['paginationPageSize'] = this.paginationParams['pageSize'];
            this.gridOptions['cacheBlockSize'] = this.paginationParams['pageSize'];
        }
    };
    Object.defineProperty(CsAgGridJsComponent.prototype, "focusEnablingClass", {
        get: function () {
            var enablingClass = '';
            var isSpace = function () { return enablingClass ? ' ' : ''; };
            enablingClass += this.config.gridConfig.rowFocus ? (isSpace() + 'cs-enable-row-focus') : '';
            enablingClass += this.config.gridConfig.cellFocus ? (isSpace() + 'cs-enable-cell-focus') : '';
            return enablingClass;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CsAgGridJsComponent.prototype, "isPagination", {
        get: function () {
            return !this.isCSRMEnabled && !this.config.hidePagination;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CsAgGridJsComponent.prototype, "isCSRMEnabled", {
        get: function () {
            return this.config.gridConfig.enableClientSideRowModel;
        },
        enumerable: true,
        configurable: true
    });
    CsAgGridJsComponent.prototype.resolvedAngularComponents = function () {
        var _this = this;
        if (this.config['customComponents']) {
            var resolvedComponents_1 = {};
            Object.entries(this.config.customComponents).forEach(function (_a) {
                var name = _a[0], component = _a[1];
                resolvedComponents_1[name] = _this._componentResolver.resolveAngularRendererComponent(component);
            });
            return resolvedComponents_1;
        }
        return null;
    };
    CsAgGridJsComponent.prototype.setRowSelectionConfig = function () {
        var _this = this;
        if (this.config.gridConfig.rowSelection) {
            this.gridOptions['rowSelection'] = {
                mode: 'multiRow',
                groupSelects: "descendants",
                headerCheckbox: this.config.gridConfig.hideHeaderRowSelection ? false : true,
                isRowSelectable: this.config.gridConfig.hasOwnProperty("isRowSelectable") ? this.config.gridConfig.isRowSelectable : function () { return true; },
            };
        }
        if (this.config.gridConfig.pinRowSelection) {
            this.gridOptions['selectionColumnDef'] = {
                pinned: this.config.gridConfig.pinRowSelection,
            };
        }
        // Added listener to handle group selection
        if (this.config.gridConfig.uniqueRowKeyField) {
            var keyField_1 = this.config.gridConfig.uniqueRowKeyField;
            var rowMatchesGroupPath_1 = function (row, keys, fields) {
                if (!row || !Array.isArray(keys) || !Array.isArray(fields) || keys.length !== fields.length) {
                    return false;
                }
                for (var i = 0; i < keys.length; i++) {
                    if (row[fields[i]] !== keys[i]) {
                        return false;
                    }
                }
                return true;
            };
            this.gridOptions['onRowSelected'] = function (event) {
                var isGroupHead = 'expanded' in event.node && !event.node.expanded;
                var isSelected = event.node.__selected;
                var buildGroupPath = function (node) {
                    var keys = [];
                    var fields = [];
                    while (node) {
                        if (node.key !== undefined && node.field !== undefined) {
                            keys.unshift(node.key);
                            fields.unshift(node.field);
                        }
                        node = node.parent;
                    }
                    return { keys: keys, fields: fields };
                };
                var updateSelection = function (rows, add) {
                    var rowMap = new Map(_this.currentlySelectedRows.map(function (row) { return [row[keyField_1], row]; }));
                    rows.forEach(function (row) {
                        var key = row[keyField_1];
                        if (key !== undefined && key !== null && key !== '') {
                            if (add)
                                rowMap.set(key, row);
                            else
                                rowMap.delete(key);
                        }
                    });
                    _this.currentlySelectedRows = Array.from(rowMap.values());
                };
                if (isGroupHead && isSelected) {
                    var _a = buildGroupPath(event.node), keys = _a.keys, fields = _a.fields;
                    _this.loadGroupChildren(keys, fields).then(function (children) {
                        updateSelection([event.data].concat(children || []), true);
                        _this.cdr.markForCheck();
                    });
                }
                else if (!isSelected) {
                    if (isGroupHead) {
                        var _b = buildGroupPath(event.node), keys_1 = _b.keys, fields_1 = _b.fields;
                        // Remove group row and any cached rows matching this group path (no API call)
                        var matchingRows = _this.currentlySelectedRows.filter(function (row) { return row !== event.data && rowMatchesGroupPath_1(row, keys_1, fields_1); });
                        var rowsToRemove = [event.data].concat(matchingRows);
                        updateSelection(rowsToRemove, false);
                        _this.cdr.markForCheck();
                    }
                    else {
                        updateSelection([event.data], false);
                    }
                }
                _this.rowSelection(event);
                _this.cdr.markForCheck();
            };
        }
    };
    CsAgGridJsComponent.prototype.loadGroupChildren = function (groupKeys, rowGroupCols) {
        var _this = this;
        // Example: call same API your SSRM datasource uses
        var reqParams = {
            page: this.isPagination ? (this.paginationParams.currentPage || 1) : null,
            limit: this.paginationParams.pageSize,
            groupKeys: groupKeys ? JSON.stringify(groupKeys) : null,
            rowGroupCols: groupKeys ? JSON.stringify(rowGroupCols) : null,
        };
        this.isSelectionLoading = true;
        return new Promise(function (resolve, reject) {
            _this.config.gridConfig.gridList({ reqParams: reqParams }).subscribe(function (res) {
                var data = res[_this.config.gridConfig.dataResKeys.dataKey] || [];
                resolve(data);
                _this.isSelectionLoading = false; // Done loading
            }, function (err) {
                reject(err);
                _this.isSelectionLoading = false; // Fail, still stop loading
            });
        });
    };
    CsAgGridJsComponent.prototype.setDetailGridConfig = function () {
        var _this = this;
        if (this.config.gridConfig.enableDetailGrid && this.config.detailGridConfig) {
            var detailConfig = this.config.detailGridConfig;
            var paramsCb_1 = detailConfig.params;
            var rendererCb_1 = detailConfig.renderer;
            // const heightCb = detailConfig.gridHeight;
            this.gridOptions['detailCellRenderer'] = function (rendererParams) {
                var detailMetaData = rendererParams.node.parent['detailMetaData'];
                // const detailHeight: ICsDetailGridHeightObject | number = heightCb ? heightCb(detailMetaData) : 310;
                var detailRenderer = rendererCb_1 ? rendererCb_1(detailMetaData) : 'detailGridRenderer';
                var RendererClass = _this.gridOptions.components[detailRenderer];
                var instance = new RendererClass();
                var rowId = (rendererParams.node.id || rendererParams.node.rowIndex).replace("detail_", "");
                _this.gridApiMap.set(rowId, _this.gridApi);
                rendererParams['currentParent'] = _this.params && _this.params['parentTree']
                    ? _this.params['parentTree'].concat([{ api: _this.gridApi, rowId: rowId, config: _this.config }])
                    : [{ api: _this.gridApi, rowId: rowId, config: _this.config }];
                instance.init(rendererParams);
                // const rowNodes = this.getDisplayedRowNodes(rendererParams.api) || [];
                //
                // const computedHeight = typeof detailHeight == 'number' ? detailHeight : Math.min(
                //     (detailHeight.topBarHeight || 0) + detailHeight.headerHeight + (detailHeight.padding || 0) + rowNodes.length * detailHeight.rowHeight,
                //     detailHeight.maxHeight
                // );
                // setTimeout(() => {
                //     rendererParams.node.setRowHeight(computedHeight);
                //     rendererParams.api.onRowHeightChanged();
                // });
                return instance.getGui();
            };
            this.gridOptions['detailCellRendererParams'] = function (params) {
                return { config: paramsCb_1(params.node.parent['detailMetaData']) };
            };
        }
    };
    CsAgGridJsComponent.prototype.setGridLicense = function () {
        var agGridGlobal = window.agGrid;
        if (agGridGlobal && agGridGlobal.LicenseManager && typeof agGridGlobal.LicenseManager.setLicenseKey === 'function') {
            agGridGlobal.LicenseManager.setLicenseKey(environment.agGridEnterpriseLicenseKey);
        }
    };
    CsAgGridJsComponent.prototype.createGrid = function () {
        var agGridGlobal = window.agGrid;
        if (!agGridGlobal || typeof agGridGlobal.createGrid !== 'function') {
            console.error('[CsAgGridJs #' + this.instanceId + '] create: FAILED — window.agGrid missing. Include ag-grid-enterprise.min.js in index.html before the app boots.');
            return;
        }
        if (!this.grid || !this.grid.nativeElement) {
            this.dbg('create: skipped — #grid element not ready');
            return;
        }
        try {
            this.dbg('create: calling agGrid.createGrid()', {
                rowModel: this.isCSRMEnabled ? 'clientSide' : 'serverSide',
                isDetailGrid: this.isDetailGrid
            });
            this.gridApi = agGridGlobal.createGrid(this.grid.nativeElement, this.gridOptions);
            if (!this.gridApi) {
                console.warn('[CsAgGridJs #' + this.instanceId + '] create: createGrid returned no api');
                return;
            }
            this.dbg('create: grid instance ready; emitting onGridInit');
            this.onGridInit.emit(this.gridInstance);
            this.updateDragListenersToHeaders();
            this.observeGridContainerResize();
            this.showGridSkeleton = false;
            this.cdr.detectChanges();
        }
        catch (error) {
            console.error('[CsAgGridJs #' + this.instanceId + '] create: error', error);
        }
    };
    CsAgGridJsComponent.prototype.observeGridContainerResize = function () {
        var _this = this;
        var gridEl = this.grid.nativeElement;
        var containerToObserve = gridEl.closest('.grid-wrapper') || gridEl.parentElement || gridEl;
        var ResizeObserverCtor = window.ResizeObserver;
        if (!containerToObserve || !ResizeObserverCtor) {
            return;
        }
        this.resizeObserver = new ResizeObserverCtor(function () {
            clearTimeout(_this.resizeDebounceTimer);
            _this.resizeDebounceTimer = setTimeout(function () {
                requestAnimationFrame(function () {
                    if (!_this.gridApi) {
                        return;
                    }
                    _this.updateDomLayout();
                    if (typeof _this.gridApi.sizeColumnsToFit === 'function') {
                        //this.gridApi.sizeColumnsToFit();
                    }
                    _this.cdr.markForCheck();
                });
            }, 100);
        });
        this.resizeObserver.observe(containerToObserve);
    };
    Object.defineProperty(CsAgGridJsComponent.prototype, "dataSource", {
        get: function () {
            var _this = this;
            return ({ getRows: function (params) { return _this.setGridData(params); } });
        },
        enumerable: true,
        configurable: true
    });
    CsAgGridJsComponent.prototype.setCSRMData = function () {
        if (this.isCSRMEnabled) {
            this.setGridData();
        }
    };
    Object.defineProperty(CsAgGridJsComponent.prototype, "gridInstance", {
        get: function () {
            var _this = this;
            return {
                refreshGridData: this.refreshGridData,
                addOnData: this.getAddOnData,
                getSelectedRows: this.getSelectedRows,
                resetPagination: this.resetPagination,
                refreshHeaders: this.updateDragListenersToHeaders.bind(this),
                gridApi: this.gridApi,
                getPaginationParams: function () { return ({
                    page: _this.paginationParams.currentPage != null ? _this.paginationParams.currentPage : 1,
                    limit: _this.paginationParams.pageSize != null ? _this.paginationParams.pageSize : 10,
                }); },
            };
        },
        enumerable: true,
        configurable: true
    });
    CsAgGridJsComponent.prototype.isDisable = function (action) {
        return typeof action.disable === 'function' ? action.disable() : action.disable;
    };
    CsAgGridJsComponent.prototype.isHide = function (action) {
        return typeof action.hide === 'function' ? action.hide() : action.hide;
    };
    CsAgGridJsComponent.prototype.updateSwappingRowClasses = function (event, rowElement, rowIndex) {
        var totalRows = event.api.getDisplayedRowCount();
        totalRows = totalRows > this.paginationParams.pageSize ? this.paginationParams.pageSize : totalRows;
        this.renderer[rowIndex === 0 ? 'addClass' : 'removeClass'](rowElement, 'ag-row-first');
        this.renderer[rowIndex === (totalRows - 1) ? 'addClass' : 'removeClass'](rowElement, 'ag-row-last');
        this.renderer.addClass(rowElement, (rowIndex % 2) === 0 ? 'ag-row-even' : 'ag-row-odd');
        this.renderer.removeClass(rowElement, (rowIndex % 2) === 0 ? 'ag-row-odd' : 'ag-row-even');
    };
    CsAgGridJsComponent.prototype.onRowDropped = function (event) {
        var _this = this;
        if (this.config.gridConfig.rowReOrder) {
            var swappedRows = this.getDisplayedRowNodes(event.api)
                .filter(function (rowNode) { return 'swappedOrder' in rowNode.data; })
                .map(function (rowNode) { return ({ id: rowNode.data['id'], order: rowNode.data['swappedOrder'] }); })
                .sort(function (current, next) { return current['order'] > next['order'] ? 1 : (current['order'] < next['order'] ? -1 : 0); });
            if (swappedRows.length) {
                this.config.gridConfig
                    .updateRowOrder(swappedRows)
                    .subscribe(function (res) { return _this.refreshGridData(); });
            }
        }
    };
    CsAgGridJsComponent.prototype.rowSelection = function (event) {
        var _this = this;
        if (event.source === 'uiSelectAll') {
            return;
        }
        var eventParam = {
            data: event.data,
            isSelected: event.node.isSelected()
        };
        var selectionCb = this.config.gridConfig.onRowSelection;
        this.ngZone.run(function () {
            selectionCb ? selectionCb(_this.gridInstance, eventParam) : _this.onRowSelection.emit(eventParam);
        });
    };
    CsAgGridJsComponent.prototype.allRowSelection = function (event) {
        var _this = this;
        var runUpdate = function () {
            var allSelectionCb = _this.config.gridConfig.onAllRowSelection;
            _this.ngZone.run(function () {
                allSelectionCb ? allSelectionCb(_this.gridInstance) : _this.onAllRowSelection.emit();
                _this.cdr.markForCheck();
            });
        };
        // Header checkbox (select all / uncheck all) can come through with different sources
        // depending on AG Grid version/config. Handle all of them consistently.
        if (['apiSelectAll', 'checkboxSelected', 'uiSelectAll'].includes(event.source)) {
            // Header checkbox (select all / uncheck all): sync cache and still update UI
            setTimeout(function () {
                if (_this.gridApi) {
                    var selectedNodes = _this.gridApi.getSelectedNodes() || [];
                    if (selectedNodes.length === 0) {
                        _this.currentlySelectedRows = [];
                    }
                    else {
                        // Provide one "row selection" style callback for header select-all,
                        // so consumers relying on rowSelection context (e.g. customerId) still work.
                        var firstNode = selectedNodes[0];
                        if (firstNode && firstNode.data) {
                            var selectionCb_1 = _this.config.gridConfig.onRowSelection;
                            var eventParam_1 = { data: firstNode.data, isSelected: true };
                            _this.ngZone.run(function () {
                                selectionCb_1 ? selectionCb_1(_this.gridInstance, eventParam_1) : _this.onRowSelection.emit(eventParam_1);
                            });
                        }
                    }
                }
                runUpdate();
            }, 0);
            return;
        }
        runUpdate();
    };
    Object.defineProperty(CsAgGridJsComponent.prototype, "isHeaderActionsAvailable", {
        get: function () {
            var _this = this;
            return !!(this.config.headerPanelConfig.actions.length
                && this.config.headerPanelConfig.actions.find(function (action) { return !_this.isHide(action); }));
        },
        enumerable: true,
        configurable: true
    });
    CsAgGridJsComponent.prototype.onActionClick = function (action) {
        if (this.isDisable(action)) {
            return;
        }
        action.onClick({ action: action.id });
    };
    CsAgGridJsComponent.prototype.onSearch = function (val) {
        if (typeof val === 'string') {
            this.searchText = val;
            this.searchExtraParams = {};
        }
        else {
            var searchText = val.searchText, extraParams = tslib_1.__rest(val, ["searchText"]);
            this.searchText = searchText;
            this.searchExtraParams = extraParams;
        }
        this.resetPagination();
        this.refreshGridData();
    };
    CsAgGridJsComponent.prototype.onFilter = function (param, action) {
        if (action === 'apply') {
            this.appliedFilter.push(param);
        }
        else if (action === 'remove') {
            this.appliedFilter.splice(param, 1);
        }
        else if (action === 'update') {
            var _a = param, index = _a.index, filterData = tslib_1.__rest(_a, ["index"]);
            this.appliedFilter[index] = filterData;
        }
        this.resetPagination();
        this.refreshGridData();
    };
    CsAgGridJsComponent.prototype.updatePagingParams = function (params) {
        this.paginationParams = params;
        if (params.currentEvent && params.currentEvent !== 'none') {
            this.gridApi.setGridOption('paginationPageSize', this.paginationParams.pageSize);
            this.gridApi.setGridOption('cacheBlockSize', this.paginationParams.pageSize);
            this.goToPage();
            this.refreshGridData();
        }
    };
    CsAgGridJsComponent.prototype.goToPage = function (page) {
        if (this.isPagination) {
            if (page) {
                this.paginationParams.currentPage = page;
            }
            this.gridApi.paginationGoToPage(this.paginationParams.currentPage);
        }
    };
    CsAgGridJsComponent.prototype.updateDomLayout = function () {
        var gridEl = this.grid.nativeElement;
        var gridViewportEl = gridEl.querySelector('.ag-body-viewport .ag-viewport.ag-center-cols-viewport');
        if (!gridViewportEl) {
            return;
        }
        var containerHeight = gridEl.clientHeight;
        var headerHeight = this.gridApi.getSizesForCurrentTheme().headerHeight;
        var headerBorderWidth = 1;
        var gridHeight = headerHeight + headerBorderWidth + gridViewportEl.offsetHeight;
        var domLayout = gridHeight > containerHeight ? 'normal' : 'autoHeight';
        if (domLayout !== this.gridLayout) {
            this.gridApi.setGridOption('domLayout', domLayout);
            this.gridLayout = domLayout;
            // Run change detection so [ngClass]="{'h-100': gridLayout === 'normal'}" updates
            // (onGridSizeChanged/onModelUpdated run outside Angular zone and don't trigger CD)
            this.cdr.markForCheck();
        }
        if (this.params) {
            var defaultRowHeight = 310;
            var rowNodes = this.getDisplayedRowNodes(this.gridApi);
            var detailHeight = this.params.config.gridHeight && rowNodes.length ? this.params.config.gridHeight : defaultRowHeight;
            var computedHeight = typeof detailHeight == 'number' ? detailHeight : Math.min((detailHeight.topBarHeight || 0) + detailHeight.headerHeight + (detailHeight.padding || 0) + rowNodes.length * detailHeight.rowHeight, detailHeight.maxHeight);
            // const detailGridFound = rowNodes.find((node) => node.detail === true);
            // this.sendParentNode.emit(detailGridFound);
            if (this.params.parentTree.length == 2 && this.gridComputedHeight !== computedHeight - 42) {
                var parentBeforeRowNode = this.params.parentTree[1];
                var parentBeforeRowNodes = this.getDisplayedRowNodes(parentBeforeRowNode.api);
                var count = parentBeforeRowNodes.filter(function (node) {
                    var id = node.id;
                    return typeof id === 'string' && !id.includes('detail_');
                }).length;
                var parentDetailHeight = parentBeforeRowNode.config.gridHeight && rowNodes.length ? parentBeforeRowNode.config.gridHeight : defaultRowHeight;
                var parentComputedHeight = typeof parentDetailHeight == 'number' ? parentDetailHeight : (parentDetailHeight.topBarHeight || 0) + parentDetailHeight.headerHeight + (parentDetailHeight.padding || 0) + count * parentDetailHeight.rowHeight;
                var parentHeight = typeof parentDetailHeight == 'number' ? parentDetailHeight : Math.min(((computedHeight * (parentBeforeRowNodes.length - count)) + parentComputedHeight), parentDetailHeight.maxHeight);
                var grandParentRowNode = this.params.parentTree[0];
                grandParentRowNode.api.getRowNode('detail_' + grandParentRowNode.rowId).setRowHeight(parentHeight);
                grandParentRowNode.api.onRowHeightChanged();
                this.params.node.setRowHeight(computedHeight);
                this.params.api.onRowHeightChanged();
            }
            else if (this.params.parentTree.length == 1 && this.gridComputedHeight !== computedHeight) {
                this.params.node.setRowHeight(computedHeight);
                this.params.api.onRowHeightChanged();
            }
            this.gridComputedHeight = computedHeight;
        }
    };
    CsAgGridJsComponent.prototype.setGridData = function (params) {
        var _this = this;
        var requestParams = tslib_1.__assign({ searchText: this.searchText, filters: this.appliedFilter ? JSON.stringify(this.appliedFilter) : null }, (this.config.filterPanelConfig && this.config.filterPanelConfig.showTagListDropDown ? { tagIds: '' } : {}), this.searchExtraParams);
        if (!this.isCSRMEnabled && params) {
            requestParams['page'] = this.isPagination ? (this.paginationParams.currentPage || 1) : null;
            requestParams['limit'] = this.paginationParams.pageSize;
            requestParams['sort'] = params.request.sortModel[0] ? JSON.stringify(params.request.sortModel[0]) : null;
            requestParams['groupKeys'] = params.request.groupKeys ? JSON.stringify(params.request.groupKeys) : null;
            requestParams['rowGroupCols'] = params.request.rowGroupCols
                ? JSON.stringify(params.request.rowGroupCols.map(function (item) { return item['field']; }))
                : null;
        }
        this.dbg('data: request', {
            rowModel: this.isCSRMEnabled ? 'clientSide' : 'serverSide',
            requestParams: requestParams,
            hasServerSideParams: !!params
        });
        var parentNode = params && params.parentNode;
        var parentNodeInfo = parentNode != null
            ? { expanded: !!parentNode.expanded, key: parentNode.key }
            : null;
        this.gridList$ = this.config.gridConfig.gridList({
            reqParams: requestParams,
            parentNode: parentNodeInfo
        })
            .subscribe(function (res) {
            var data = res[_this.config.gridConfig.dataResKeys.dataKey];
            _this.gridListAddOnData = tslib_1.__assign({}, res['addOnData'], { displayRecordCount: data.length });
            if (_this.isCSRMEnabled) {
                _this.totalRecords = +res[_this.config.gridConfig.dataResKeys.countKey];
                _this.showOrHideNoRowsOverlay(!_this.totalRecords);
                _this.gridApi.setGridOption('rowData', data);
            }
            else {
                var isGroupedCall = requestParams['groupKeys'] && JSON.parse(requestParams['groupKeys']).length > 0;
                var recordCount = void 0;
                if (isGroupedCall) {
                    recordCount = data.length;
                }
                else {
                    _this.totalRecords = +res[_this.config.gridConfig.dataResKeys.countKey];
                    var isPartialRecordsInPage = data.length < requestParams['limit'];
                    recordCount = isPartialRecordsInPage ? data.length : _this.totalRecords;
                }
                _this.showOrHideNoRowsOverlay(!recordCount);
                params.success({ rowData: data, rowCount: recordCount });
                // Default-select child rows when tree/group is expanded and data loaded from API
                // if (isGroupedCall && this.config.gridConfig.rowSelection && this.config.gridConfig.uniqueRowKeyField && Array.isArray(data) && data.length > 0) {
                //     const keyField = this.config.gridConfig.uniqueRowKeyField;
                //     const keySet = new Set(
                //         data
                //             .map((row: any) => row[keyField])
                //             .filter((k: any) => k !== undefined && k !== null && k !== '')
                //     );
                //     setTimeout(() => {
                //         if (!this.gridApi) return;
                //         this.gridApi.forEachNode((node: any) => {
                //             if (node.data && keySet.has(node.data[keyField])) {
                //                 node.setSelected(true);
                //             }
                //         });
                //         const rowMap = new Map<string | number, any>(
                //             this.currentlySelectedRows.map((row: any): [string | number, any] => [row[keyField], row])
                //         );
                //         data.forEach((row: any) => {
                //             const key = row[keyField];
                //             if (key !== undefined && key !== null && key !== '') {
                //                 rowMap.set(key, row);
                //             }
                //         });
                //         this.currentlySelectedRows = Array.from(rowMap.values());
                //         this.cdr.markForCheck();
                //     }, 0);
                // }
            }
            _this.isDataLoading = false;
            _this.dbg('data: loaded', {
                rowCount: data && data.length != null ? data.length : 0,
                totalRecords: _this.totalRecords,
                rowModel: _this.isCSRMEnabled ? 'clientSide' : 'serverSide'
            });
            _this.config.gridConfig.onGridDataLoad ?
                _this.config.gridConfig.onGridDataLoad(_this.gridInstance)
                : _this.onGridDataLoad.emit(_this.gridInstance);
        }, function (err) {
            console.error('[CsAgGridJs #' + _this.instanceId + '] data: load failed', err);
        });
    };
    /*******************Custom grouping drag and drop**************************/
    CsAgGridJsComponent.prototype.updateDragListenersToHeaders = function () {
        var _this = this;
        if (this.config.gridConfig.enableCustomGroupPanel) {
            setTimeout(function () {
                var headers = document.querySelectorAll('.ag-header-cell');
                headers.forEach(function (headerEl) {
                    var colId = headerEl.getAttribute('col-id');
                    if (!colId)
                        return;
                    var col = _this.gridApi.getColumn(colId);
                    var colDef = col.getColDef();
                    // Skip if column is not groupable
                    if (!colDef.enableCustomRowGroup)
                        return;
                    var labelEl = headerEl.querySelector('.ag-header-cell-label');
                    if (!labelEl)
                        return;
                    // Prevent duplicate icon
                    if (labelEl.querySelector('.cs-drag-icon'))
                        return;
                    var dragPreviewEl;
                    // Create drag icon container
                    var iconContainer = document.createElement('span');
                    iconContainer.classList.add('cs-drag-icon');
                    iconContainer.innerHTML = "<i class=\"fa-solid fa-grip-dots-vertical\" style=\"cursor: move\"></i>";
                    // ✅ Make only the icon draggable
                    iconContainer.setAttribute('draggable', 'true');
                    // Apply styling
                    iconContainer.style.display = 'flex';
                    iconContainer.style.flex = '1';
                    iconContainer.style.justifyContent = 'end';
                    // Insert before the sort icon
                    labelEl.insertBefore(iconContainer, labelEl.lastElementChild);
                    // Add dragstart and dragend handlers to the icon only
                    iconContainer.addEventListener('dragstart', function (e) {
                        e.stopPropagation(); // Prevent bubbling to header
                        e.dataTransfer.setData('application/vnd.aggrid.coldef+json', JSON.stringify(colDef));
                        e.dataTransfer.effectAllowed = 'move';
                        // Create custom drag image
                        dragPreviewEl = document.createElement('div');
                        dragPreviewEl.innerText = colDef.headerName || colId;
                        dragPreviewEl.style.padding = '4px 10px';
                        dragPreviewEl.style.background = '#e1fcda';
                        dragPreviewEl.style.border = '1px solid #c3e6cb';
                        dragPreviewEl.style.borderRadius = '4px';
                        dragPreviewEl.style.fontSize = '13px';
                        dragPreviewEl.style.fontFamily = 'inherit';
                        dragPreviewEl.style.color = '#2e7d32';
                        dragPreviewEl.style.position = 'absolute';
                        dragPreviewEl.style.top = '-1000px';
                        dragPreviewEl.id = "gridDraggedElement";
                        document.body.appendChild(dragPreviewEl);
                        e.dataTransfer.setDragImage(dragPreviewEl, 0, 0);
                    });
                    iconContainer.addEventListener('dragend', function () {
                        if (dragPreviewEl) {
                            document.body.removeChild(dragPreviewEl);
                            dragPreviewEl = null;
                        }
                    });
                });
            });
        }
    };
    CsAgGridJsComponent.prototype.resetSelectedRows = function () {
        if (this.config.gridConfig.rowSelection) {
            this.gridApi.deselectAll();
            this.currentlySelectedRows = [];
        }
    };
    CsAgGridJsComponent.prototype.ngOnDestroy = function () {
        this.dbg('destroy: teardown start — release timers, observers, subscriptions, grid api');
        if (this.resizeDebounceTimer) {
            clearTimeout(this.resizeDebounceTimer);
            this.resizeDebounceTimer = null;
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.gridList$) {
            this.gridList$.unsubscribe();
            this.gridList$ = null;
        }
        if (this.gridApiMap && this.gridApiMap.size) {
            this.gridApiMap.clear();
        }
        if (this.gridApi && typeof this.gridApi.destroy === 'function') {
            try {
                this.dbg('destroy: calling gridApi.destroy()');
                this.gridApi.destroy();
            }
            catch (e) {
                console.warn('[CsAgGridJs #' + this.instanceId + '] destroy: gridApi.destroy error', e);
            }
        }
        this.gridApi = null;
        this.showGridSkeleton = true;
        this.currentlySelectedRows = [];
        this.dbg('destroy: teardown complete — references cleared');
    };
    CsAgGridJsComponent.instanceSeq = 0;
    return CsAgGridJsComponent;
}());
export { CsAgGridJsComponent };
